/* eslint-disable @typescript-eslint/no-empty-object-type */

import type {
	EntryPoint,
	EntryPointComponent,
	JSResourceReference,
	PreloadProps,
} from 'react-relay';

// Utility type for nested entry points that supports nullable
type NullableNestedEntryPoints<T> =
	T extends EntryPointComponent<any, infer E, any, any> ? E : T extends null ? {} : never;

// NullableEntryPoint allows the root to be a JSResourceReference<EntryPointComponent<...> | null>
// This is needed in scenarios where we are intentionally excluding a component from being loaded
// e.g when using the clientSideOnly function to exclude entrypoints from being loaded on the server
export type NullableEntryPoint<TEntryPointComponent, TEntryPointParams extends {} = {}> = Readonly<{
	getPreloadProps: (
		entryPointParams: TEntryPointParams,
	) => PreloadProps<
		TEntryPointParams,
		TEntryPointComponent extends EntryPointComponent<infer TPreloadedQueries, any, any, any>
			? TPreloadedQueries
			: never,
		NullableNestedEntryPoints<TEntryPointComponent>,
		TEntryPointComponent extends EntryPointComponent<any, any, any, infer TExtraProps>
			? TExtraProps
			: never
	>;
	root: JSResourceReference<TEntryPointComponent | null>;
}>;

export function createEntryPoint<Root, EntryPointParams extends {} = {}>(
	// The param types can only be inferred if the shape of the entrypoint (i.e. root and getPreloadProps) is specified
	// within the function definition itself. Using EntryPoint, EntryPointComponent, or a custom EntryPointRepresentation
	// in place of this will cause Root to be inferred as an 'unknown' type.
	params: Root extends JSResourceReference<
		EntryPointComponent<
			infer PreloadedQueries,
			infer PreloadedEntryPoints,
			infer RuntimeProps,
			infer ExtraProps
		>
	>
		? {
				getPreloadProps: (
					entryPointParams: EntryPointParams,
				) => PreloadProps<EntryPointParams, PreloadedQueries, PreloadedEntryPoints, ExtraProps>;
				root: Root;
			}
		: Root extends JSResourceReference<EntryPointComponent<
					infer PreloadedQueries,
					infer PreloadedEntryPoints,
					infer RuntimeProps,
					infer ExtraProps
			  > | null>
			? {
					getPreloadProps: (
						entryPointParams: EntryPointParams,
					) => PreloadProps<EntryPointParams, PreloadedQueries, PreloadedEntryPoints, ExtraProps>;
					root: Root;
				}
			: never,
): Root extends JSResourceReference<
	EntryPointComponent<
		infer PreloadedQueries,
		infer PreloadedEntryPoints,
		infer RuntimeProps,
		infer ExtraProps
	>
>
	? // An EntryPoint type must be returned here instead of typeof params, so that it can be inferred correctly throughout
		// other @types/react-relay usages when using entrypoints
		EntryPoint<
			EntryPointComponent<PreloadedQueries, PreloadedEntryPoints, RuntimeProps, ExtraProps>,
			EntryPointParams
		>
	: Root extends JSResourceReference<EntryPointComponent<
				infer PreloadedQueries,
				infer PreloadedEntryPoints,
				infer RuntimeProps,
				infer ExtraProps
		  > | null>
		? NullableEntryPoint<
				EntryPointComponent<PreloadedQueries, PreloadedEntryPoints, RuntimeProps, ExtraProps>,
				EntryPointParams
			>
		: never {
	return params;
}
